import Text from '@/components/common/Text';
import { Theme } from '@/styles/Theme';
import type { UserDetail } from '@/types/admin.types';
import styled from '@emotion/styled';

interface UserInfoCardProps {
  user: UserDetail;
}

const getSubscriptionStatusBadge = (status: UserDetail['subscription_status']) => {
  const statusMap = {
    active: { text: '유료', color: '#4CAF50' },
    expired: { text: '만료됨', color: '#F44336' },
    free: { text: '무료', color: '#666' },
  };
  return statusMap[status || 'free'] || statusMap.free;
};

const formatDate = (dateString: string | null) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

const formatDateTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const UserInfoCard = ({ user }: UserInfoCardProps) => {
  const statusBadge = getSubscriptionStatusBadge(user.subscription_status);

  return (
    <Container>
      {/* Basic Info Section */}
      <Section>
        <SectionHeader>
          <Text fontSize='1.125rem' fontWeight={700}>
            기본 정보
          </Text>
        </SectionHeader>
        <InfoGrid>
          <InfoRow>
            <Label>회사명:</Label>
            <Value>{user.company}</Value>
          </InfoRow>
          <InfoRow>
            <Label>이메일:</Label>
            <Value>{user.email}</Value>
          </InfoRow>
          <InfoRow>
            <Label>연락처:</Label>
            <Value>{user.phone || '-'}</Value>
          </InfoRow>
          <InfoRow>
            <Label>구독 상태:</Label>
            <Value>
              <StatusBadge color={statusBadge.color}>
                {statusBadge.text}
              </StatusBadge>
            </Value>
          </InfoRow>
          <InfoRow>
            <Label>결제 방식:</Label>
            <Value>
              {user.payment_method === 'prepaid'
                ? '선불'
                : user.payment_method === 'postpaid'
                ? '후불'
                : '-'}
            </Value>
          </InfoRow>
          <InfoRow>
            <Label>관리자 여부:</Label>
            <Value>
              {user.is_admin ? (
                <StatusBadge color={Theme.colors.B_1}>관리자</StatusBadge>
              ) : (
                '-'
              )}
            </Value>
          </InfoRow>
        </InfoGrid>
      </Section>

      {/* Subscription Info Section */}
      <Section>
        <SectionHeader>
          <Text fontSize='1.125rem' fontWeight={700}>
            구독 정보
          </Text>
        </SectionHeader>
        <InfoGrid>
          <InfoRow>
            <Label>구독 시작일:</Label>
            <Value>{formatDate(user.subscription_start_date)}</Value>
          </InfoRow>
          <InfoRow>
            <Label>구독 종료일:</Label>
            <Value>{formatDate(user.subscription_end_date)}</Value>
          </InfoRow>
          <InfoRow>
            <Label>작업자 수:</Label>
            <Value fontWeight={700}>{user.worker_count}명</Value>
          </InfoRow>
          <InfoRow>
            <Label>작업자당 가격:</Label>
            <Value>
              {user.price_per_worker
                ? `${user.price_per_worker.toLocaleString('ko-KR')}원`
                : '-'}
            </Value>
          </InfoRow>
        </InfoGrid>
      </Section>

      {/* Limits Info Section */}
      <Section>
        <SectionHeader>
          <Text fontSize='1.125rem' fontWeight={700}>
            제한 정보
          </Text>
        </SectionHeader>
        <InfoGrid>
          <InfoRow>
            <Label>무료 작업자 제한:</Label>
            <Value>{user.free_workers_limit}명</Value>
          </InfoRow>
          <InfoRow>
            <Label>무료 검사 제한:</Label>
            <Value>{user.free_inspections_limit}건</Value>
          </InfoRow>
          <InfoRow>
            <Label>승인 상태:</Label>
            <Value>
              {user.is_approved ? (
                <StatusBadge color={Theme.colors.GREEN}>승인됨</StatusBadge>
              ) : (
                <StatusBadge color={Theme.colors.WARNING_RED}>미승인</StatusBadge>
              )}
            </Value>
          </InfoRow>
          <InfoRow>
            <Label>무료 체험:</Label>
            <Value>
              {user.is_trial ? (
                <StatusBadge color={Theme.colors.B_3}>체험 중</StatusBadge>
              ) : (
                '-'
              )}
            </Value>
          </InfoRow>
        </InfoGrid>
      </Section>

      {/* Date Info Section */}
      <Section>
        <SectionHeader>
          <Text fontSize='1.125rem' fontWeight={700}>
            날짜 정보
          </Text>
        </SectionHeader>
        <InfoGrid>
          <InfoRow>
            <Label>가입일:</Label>
            <Value>{formatDateTime(user.created_at)}</Value>
          </InfoRow>
          <InfoRow>
            <Label>최근 접속일:</Label>
            <Value>{formatDateTime(user.updated_at)}</Value>
          </InfoRow>
        </InfoGrid>
      </Section>
    </Container>
  );
};

const Container = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0.75rem;
  border-bottom: 0.0625rem solid ${Theme.colors.G_4};
`;

const StatusBadge = styled.span<{ color: string }>`
  padding: 0.375rem 0.75rem;
  border-radius: 0.25rem;
  background-color: ${(props) => props.color};
  color: white;
  font-size: 0.875rem;
  font-weight: 600;
  display: inline-block;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled(Text)`
  font-size: 0.875rem;
  color: ${Theme.colors.G_2};
  font-weight: 600;
`;

const Value = styled(Text)<{ fontWeight?: number }>`
  font-size: 0.875rem;
  color: ${Theme.colors.G_1};
  font-weight: ${(props) => props.fontWeight || 400};
`;

export default UserInfoCard;
